import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as Alert } from "../../../assets/svg/alert.svg";
import { ConnectWalletContext } from "../../../contexts/ConnectWalletContext";
import { getAccountSurfaceFlags } from "../../../config/features";
import { useGetAccount } from "../../../hooks/api/account/useGetAccount";
import { accountLiquidationProximity } from "../../../utils/accountUnknowns";
import { AddMarginButton, Banner } from "./style";

// Show the bar once the account is >= 90% of the way to liquidation, and only
// hide it once it recovers below 85%. The 5% gap is hysteresis so the bar does
// not flicker on/off while margin hovers right at the threshold. Mirrors the
// backend startLiquidationWarningPoller tiers (proximity = maintenanceMargin /
// equity * 100, i.e. maintenanceMarginUtilization; 100% = liquidation).
const SHOW_AT = 90;
const HIDE_BELOW = 85;

function LiquidationRiskBanner() {
  const { t } = useTranslation("app", { keyPrefix: "LiquidationRiskBanner" });
  const { data: accountData } = useGetAccount();
  const { openDepositFlow } = useContext(ConnectWalletContext);
  const { showDerivativeUi } = getAccountSurfaceFlags(accountData?.positions);

  // `undefined` when the backend cannot value the account (null equity /
  // maintenance margin while a position is unpriced). `Number(null)` made equity
  // 0 and fired a false "100% of the way to liquidation" alarm.
  const proximity = useMemo(
    () => accountLiquidationProximity(accountData),
    [accountData]
  );

  const [visible, setVisible] = useState(false);
  useEffect(() => {
    setVisible((prev) => {
      // Unknown neither raises nor clears the warning.
      if (proximity === undefined) return prev;
      if (proximity >= SHOW_AT) return true;
      if (proximity < HIDE_BELOW) return false;
      return prev;
    });
  }, [proximity]);

  // Keep the latest proximity in a ref so the title interval reads the live
  // value without re-subscribing on every margin tick.
  const proximityRef = useRef(proximity);
  proximityRef.current = proximity;

  // Tab-title alert — only while the tab is BACKGROUNDED (that is the
  // "don't forget" case). On the active tab the red bar is already on screen,
  // so we leave document.title to react-helmet (<Meta/>), which owns it and
  // updates it on mark-price ticks — flashing it there would just fight Helmet
  // and flicker. While hidden, flash "⚠ NN% · LIQ RISK" once a second and
  // restore the page title on return / recovery / unmount.
  const showBanner = visible && showDerivativeUi;

  useEffect(() => {
    if (!showBanner) return undefined;

    let intervalId: number | undefined;
    let savedTitle = document.title;
    let flashed = false;

    const stop = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
        document.title = savedTitle;
      }
    };
    const start = () => {
      if (intervalId !== undefined) return;
      // Capture the freshest page title at the moment the user tabs away.
      savedTitle = document.title;
      flashed = false;
      const tick = () => {
        flashed = !flashed;
        const pct = proximityRef.current;
        document.title = flashed
          ? `⚠ ${pct === undefined ? "" : `${Math.round(pct)}% · `}LIQ RISK`
          : savedTitle;
      };
      tick();
      intervalId = window.setInterval(tick, 1000);
    };
    const onVisibility = () => {
      if (document.hidden) start();
      else stop();
    };

    if (document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      stop();
    };
  }, [showBanner]);

  if (!showBanner) return null;

  return (
    <Banner role="alert">
      <Alert />
      <span>
        {proximity === undefined
          ? t("message_unknown", {
              defaultValue:
                "Liquidation risk — your margin health can't be measured right now.",
            })
          : t("message", {
              defaultValue:
                "Liquidation risk — you are {{pct}}% of the way to liquidation.",
              pct: Math.round(proximity),
            })}
      </span>
      <AddMarginButton type="button" onClick={() => openDepositFlow()}>
        {t("add_margin", { defaultValue: "Add margin" })}
      </AddMarginButton>
    </Banner>
  );
}

export default LiquidationRiskBanner;
